<template>
  <div class="data-table">
    <HeaderPanel
      class="data-table__header-panel"
      v-if="headerIcon || headerText"
      :header-icon="headerIcon"
      :header-text="headerText"
    >
      <table-search
        slot="header-search"
        @search="search"
        :table-search-icon="tableSearchIcon"
      />

      <slot class="data-table__add-button" name="add" />
      <template slot="activator">
        <slot name="activator"></slot>
      </template>

      <per-page-select
        :options="itemsPerPageOptions"
        :default="itemsPerPage"
        :perPageDesc="perPageDesc"
        class="select"
        @input="changeItemsPerPage"
        slot="header-items-per-page"
      />
      <v-data-table
        slot="header-pagination"
        :height="0"
        :page.sync="page"
        :items-per-page="10"
        :items="items"
        :footer-props="footerProps"
        :options.sync="options"
        :server-items-length="count"
        dense
      >
        <template v-slot:footer></template>
      </v-data-table>
    </HeaderPanel>
    <div v-else>
      <slot name="actions" />
      <div v-if="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm">
        <slot name="table-filter" />
      </div>
    </div>
    <v-data-table
      :page.sync="page"
      :headers="headers"
      :items="items"
      :hide-default-footer="hideFooter"
      :disable-pagination="disablePagination"
      :footer-props="footerProps"
      :options.sync="options"
      :server-items-length="count"
      disable-sort
      mobile-breakpoint="769"
      single-select
      :loading="!loaded"
      @click:row="rowClick"
      dense
    >
      <template v-if="!loaded" v-slot:body>
        <div class="data-table__loader">
          <ContentLoader viewBox="0 0 100 100">
            <rect x="0" y="15" rx="5" ry="5" width="220" height="10" />
            <rect x="0" y="45" rx="5" ry="5" width="220" height="10" />
            <rect x="0" y="75" rx="5" ry="5" width="220" height="10" />
            <rect x="0" y="105" rx="5" ry="5" width="220" height="10" />
          </ContentLoader></div
      ></template>
      <template
        v-for="(slot, name) in $scopedSlots"
        v-slot:[name]="item"
      >
        <slot :name="name" v-bind="item"></slot>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import debounce from "lodash.debounce";

export default {
  props: {
    loaded: {
      type: Boolean,
      default: false,
    },
    itemKey: {
      type: String,
      default: "ID",
    },
    showExpand: {
      type: Boolean,
      default: true,
    },
    singleExpand: {
      type: Boolean,
      default: true,
    },
    headerIcon: {
      type: String,
      default: "",
    },
    headerText: {
      type: String,
      default: "",
    },
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    showFilter: {
      type: Boolean,
      default: false,
    },
    tableSearchIcon: {
      type: String,
      default: "fa-search",
    },
    count: {
      type: Number,
    },
    perPageDesc: {
      type: String,
      default: "items",
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    disablePagination: {
      type: Boolean,
      default: false,
    },
    tableSearch: {
      type: Boolean,
      default: true,
    },
    showSelectedRow: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    options: {
      handler(_, newVal) {
        if (
          this.previousPage === this.options.page &&
          this.previousLimit === this.options.itemsPerPage
        ) {
          return;
        }
        this.previousPage = this.options.page;
        this.previousLimit = this.options.itemsPerPage;
        if (newVal?.itemsPerPage === undefined) return;
        this.$emit("refresh-data", this.options);
      },
    },
    searchTerm: {
      handler() {
        this.options.q = this.searchTerm;
        this.$emit("refresh-data", this.options);
      },
    },
  },
  components: {
    PerPageSelect: () => import("@/components/DataTable/PerPageSelect.vue"),
    TableSearch: () => import("@/components/DataTable/TableSearch.vue"),
    HeaderPanel: () => import("@/components/HeaderPanel/HeaderPanel.vue"),
  },
  data() {
    return {
      selectedId: -1,
      previousLimit: null,
      previousPage: null,
      searchTerm: "",
      page: 1,
      pageCount: 3,
      itemsPerPageOptions: ["10", "20", "50"],
      itemsPerPage: "10",
      htmlContent: "This <input type='checkbox' v-on:change='onchange'>",
      options: {},
      expanded: [],
      footerProps: {
        showFirstLastPage: true,
      },
    };
  },
  computed: {
    isDarkMode() {
      return this.$store.state.mode.darkMode;
    },
  },
  methods: {
    changeItemsPerPage(itemsPerPage) {
      this.options.itemsPerPage = parseInt(itemsPerPage);
    },
    search: debounce(function (val) {
      this.searchTerm = val;
    }, 750),
    rowClick: function (_item, row) {
      if (this.showSelectedRow) {
        row.select(true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table {
  max-width: 1440px;
}

::v-deep .v-select__selections {
  min-height: 30px;
}

::v-deep .v-data-table-header-mobile {
  display: none !important;
}

::v-deep .theme--dark.v-btn.v-btn .v-icon,
.theme--dark.v-btn.v-btn.v-btn__loading {
  color: var(--apio-shade-2) !important;
  background: var(--apio-shade-2);
}

::v-deep .theme--dark.v-btn.v-btn .v-icon,
.theme--dark.v-btn.v-btn .v-btn__loading {
  color: var(--white) !important;
  background: var(--apio-shade-2);
}

::v-deep .v-btn--icon.v-size--default .v-icon {
  font-size: 18px;
}

::v-deep tr.v-data-table__selected {
  background: var(--apio-shade-4) !important;
}


@media (max-width: 768px) {
  ::v-deep
    .theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr
    > td:last-child {
    border-bottom: 1px solid var(--apio-shade-4);
  }

  ::v-deep tbody {
    background: var(--apio-shade-5);
  }

  ::v-deep
    .theme--dark.v-data-table
    > .v-data-table__wrapper
    .v-data-table__mobile-table-row {
    display: block;
    background: var(--apio-shade-2);
    border: 1px solid var(--apio-shade-4);
    border-radius: 4px;
    border-bottom: 0;
    margin-bottom: 8px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  ::v-deep
    .theme--light.v-data-table
    > .v-data-table__wrapper
    .v-data-table__mobile-table-row {
    display: block;
    color: var(--apio-shade-2);
    background: white;
    border: 1px solid var(--apio-shade-4);
    border-radius: 4px;
    border-bottom: 0;
    margin-bottom: 8px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  // DIVIDER BETWEEN ROWS FOR MOBILE MODE
  ::v-deep .v-data-table thead tr:last-child th,
  ::v-deep .v-data-table tbody tr:not(:last-child) td:last-child,
  ::v-deep .v-data-table tbody tr td,
  ::v-deep
    .v-data-table
    tbody
    tr:not(:last-child)
    td:not(.v-data-table__mobile-row) {
    border-bottom: 1px solid var(--apio-shade-5);
  }
}

.data-table {
  padding: 0 32px;

  &__loader {
    background: var(--white);
    padding: 16px;
  }

  @media screen and (max-width: 1024px) {
    padding: 0 20px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  &__header-inner {
    display: flex;
  }
  &__header-panel {
    margin-bottom: 16px;
    @media screen and (max-width: 768px) {
      margin-bottom: 0;
    }
  }
  &__top-pagination {
    height: 100%;
    &__wrapper {
      margin: auto;

      margin-bottom: 8px;

      > table {
        background: var(--apio-shade-5);
      }
    }
  }
  &__add-button {
    > button {
      margin: 0 16px;
    }
  }
}

/**
  * PAGINATION OVERRIDES
  */

::v-deep .v-data-footer {
  padding: 16px 0 !important;
  border-top: 0;
}

::v-deep .v-data-table .v-data-footer {
  border: none;
}

::v-deep .theme--light.v-data-table .v-data-footer {
  background: var(--apio-shade-5);
}
::v-deep .theme--dark.v-data-table .v-data-footer {
  background: var(--apio-shade-1);
}

::v-deep div.data-table__top-pagination__wrapper {
  border-top-width: 0 !important;
  > .v-data-table .v-data-footer {
    border-top-width: 0 !important;
  }
}

::v-deep .v-data-footer__pagination {
  margin: 0 !important;
}

::v-deep .v-data-footer__select {
  order: 2;
  display: none;
}

::v-deep .v-data-footer__icons-before {
  order: 1;
}

::v-deep .v-data-footer__pagination {
  order: 3;
}

::v-deep .v-data-footer__icons-after {
  order: 4;
}

/**
  * FINISH PAGINATION OVERRIDES
  */
</style>
